import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { ChevronLeft, ChevronRight, Save, Send, Check, AlertCircle, Plus, Trash2, Upload, X, Sparkles } from "lucide-react";

const NZ_REGIONS = [
  "Northland", "Auckland", "Waikato", "Bay of Plenty", "Gisborne", "Hawke's Bay",
  "Taranaki", "Manawatū-Whanganui", "Wellington", "Tasman", "Nelson", "Marlborough",
  "West Coast", "Canterbury", "Otago", "Southland",
];

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const STEPS = [
  { id: "about", label: "About You" },
  { id: "land", label: "Land & Experience" },
  { id: "logistics", label: "Logistics" },
  { id: "plan", label: "Bloom Club Plan" },
  { id: "references", label: "References" },
  { id: "story", label: "Your Story" },
  { id: "photos", label: "Photos" },
  { id: "review", label: "Review & Submit" },
];

const blank = () => ({
  full_legal_name: "",
  phone: "",
  farm_name: "",
  address_line: "",
  town_city: "",
  region: "",
  postcode: "",
  ownership: "",
  growing_area_sqm: 0,
  years_growing: 0,
  dahlia_years: 0,
  crops_grown: [],
  current_outlets: [],
  scale_description: "",
  has_refrigeration: false,
  has_packing_space: false,
  has_reliable_vehicle: false,
  can_deliver_locally: false,
  delivery_radius_km: 0,
  target_subscribers: 0,
  season_start_month: "",
  season_end_month: "",
  weekly_capacity_boxes: 0,
  references: [
    { name: "", role: "", email: "", phone: "", relationship: "" },
    { name: "", role: "", email: "", phone: "", relationship: "" },
  ],
  why_join: "",
  vision_statement: "",
  unique_strengths: "",
  photos: [],
});

export default function ApplicationWizard({ application, onSaved, onSubmitted }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ ...blank(), ...(application?.data || {}) });
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savedAt, setSavedAt] = useState(application?.updated_at || null);
  const [err, setErr] = useState("");
  const [missing, setMissing] = useState(application?.completion?.missing || []);
  const [autoSaveStatus, setAutoSaveStatus] = useState("idle"); // idle | pending | saving | saved
  const dirtyRef = useRef(false);
  const lastSavedDataRef = useRef(JSON.stringify(application?.data || {}));
  const autoSaveTimerRef = useRef(null);

  // ensure photos & references arrays exist
  useEffect(() => {
    setData((d) => ({
      ...d,
      references: d.references && d.references.length ? d.references : blank().references,
      photos: d.photos || [],
      crops_grown: d.crops_grown || [],
      current_outlets: d.current_outlets || [],
    }));
  }, []);

  // Debounced auto-save: whenever data changes, schedule a save 1.2s later
  useEffect(() => {
    const snapshot = JSON.stringify(data);
    if (snapshot === lastSavedDataRef.current) return;
    dirtyRef.current = true;
    setAutoSaveStatus("pending");
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      if (!dirtyRef.current) return;
      setAutoSaveStatus("saving");
      const ok = await save(true);
      if (ok) {
        lastSavedDataRef.current = JSON.stringify(data);
        dirtyRef.current = false;
        setAutoSaveStatus("saved");
        setTimeout(() => setAutoSaveStatus("idle"), 1500);
      } else {
        setAutoSaveStatus("idle");
      }
    }, 1200);
    return () => clearTimeout(autoSaveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const set = (patch) => setData((d) => ({ ...d, ...patch }));

  const save = async (silent = false) => {
    if (!silent) setSaving(true);
    setErr("");
    try {
      const { data: resp } = await api.put("/me/application", { data });
      setSavedAt(resp.application.updated_at);
      setMissing(resp.application.completion.missing);
      onSaved?.(resp.application);
      return resp.application;
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
      return null;
    } finally {
      if (!silent) setSaving(false);
    }
  };

  const goNext = async () => {
    await save(true);
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  const goPrev = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setSubmitting(true);
    setErr("");
    const ok = await save(true);
    if (!ok) {
      setSubmitting(false);
      return;
    }
    try {
      const { data: resp } = await api.post("/me/application/submit");
      onSaved?.(resp.application);
      onSubmitted?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.missing) {
        setMissing(d.missing);
        setErr(`Application incomplete. ${d.missing.length} item(s) need attention.`);
        setStep(STEPS.length - 1);
      } else {
        setErr(formatApiError(d));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const StepPanel = STEP_RENDERERS[STEPS[step].id];

  // 12 required slots total per backend `required_field_status`
  const TOTAL_REQUIRED = 12;
  const completed = Math.max(0, TOTAL_REQUIRED - (missing?.length || TOTAL_REQUIRED));
  const completePct = Math.round((completed / TOTAL_REQUIRED) * 100);

  const autoSaveText = autoSaveStatus === "saving" ? "Saving…"
    : autoSaveStatus === "saved" ? "Saved ✓"
    : autoSaveStatus === "pending" ? "Unsaved changes…"
    : (savedAt ? `Last saved · ${new Date(savedAt).toLocaleTimeString()}` : "Not yet saved");

  return (
    <div data-testid="application-wizard">
      {/* Top status bar */}
      <div className="mb-5 flex items-center justify-between gap-4 flex-wrap" data-testid="wizard-statusbar">
        <div className="flex items-center gap-3" data-testid="wizard-completeness">
          <div className="flex items-center gap-1 text-[var(--bloom-brand)]">
            <Sparkles size={14} strokeWidth={1.5} />
            <span className="font-serif italic text-lg">{completed}<span className="text-[var(--bloom-ink-muted)] text-sm">/{TOTAL_REQUIRED}</span></span>
          </div>
          <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)]">required fields complete</span>
          <div className="hidden md:block w-40 h-1 bg-[var(--bloom-border)] overflow-hidden ml-2">
            <div className="h-full bg-[var(--bloom-brand)]" style={{ width: `${completePct}%`, transition: "width 0.4s" }} />
          </div>
        </div>
        <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)]" data-testid="wizard-autosave">
          {autoSaveText}
        </div>
      </div>

      {/* Stepper */}
      <div className="grid grid-cols-4 md:grid-cols-8 gap-2 mb-10" data-testid="wizard-stepper">
        {STEPS.map((s, i) => {
          const isActive = i === step;
          const isDone = i < step;
          return (
            <button
              key={s.id}
              onClick={() => setStep(i)}
              className="text-left group"
              data-testid={`step-tab-${s.id}`}
            >
              <div className={`h-[3px] w-full mb-2 ${isActive ? "bg-[var(--bloom-brand)]" : isDone ? "bg-[var(--bloom-ink-soft)]" : "bg-[var(--bloom-border)]"}`} />
              <div className={`text-[10px] tracking-[0.2em] uppercase ${isActive ? "text-[var(--bloom-ink)]" : "text-[var(--bloom-ink-muted)]"}`}>
                {String(i + 1).padStart(2, "0")} · {s.label}
              </div>
            </button>
          );
        })}
      </div>

      <div className="bloom-card p-8 md:p-12">
        <div className="mb-8">
          <div className="bloom-label mb-2">Step {step + 1} of {STEPS.length}</div>
          <h2 className="font-serif text-3xl md:text-4xl">{STEPS[step].label}</h2>
        </div>

        <StepPanel data={data} set={set} missing={missing} />

        {err && (
          <div className="mt-6 flex items-start gap-2 text-sm text-[var(--bloom-accent)] font-serif italic">
            <AlertCircle size={16} className="mt-0.5" />
            <span>{err}</span>
          </div>
        )}

        <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-[var(--bloom-border)] pt-6">
          <div className="text-[11px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)]">
            Drafts auto-save as you type
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => save(false)}
              disabled={saving}
              className="bloom-btn bloom-btn-ghost"
              data-testid="save-draft-btn"
            >
              <Save size={14} /> {saving ? "Saving…" : "Save Draft"}
            </button>
            {step > 0 && (
              <button onClick={goPrev} className="bloom-btn bloom-btn-outline" data-testid="wizard-prev-btn">
                <ChevronLeft size={14} /> Back
              </button>
            )}
            {step < STEPS.length - 1 ? (
              <button onClick={goNext} className="bloom-btn bloom-btn-primary" data-testid="wizard-next-btn">
                Continue <ChevronRight size={14} />
              </button>
            ) : (
              <button onClick={submit} disabled={submitting} className="bloom-btn bloom-btn-primary" data-testid="wizard-submit-btn">
                <Send size={14} /> {submitting ? "Submitting…" : "Submit Application"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ----------------- Field helpers -----------------
function Field({ label, hint, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="bloom-label mb-2">{label}</div>
      {children}
      {hint && <div className="text-xs italic text-[var(--bloom-ink-muted)] mt-1 font-serif">{hint}</div>}
    </div>
  );
}

function Text({ value, onChange, placeholder, type = "text", testid }) {
  return (
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(type === "number" ? Number(e.target.value || 0) : e.target.value)}
      placeholder={placeholder}
      className="bloom-input"
      data-testid={testid}
    />
  );
}

function Textarea({ value, onChange, placeholder, rows = 5, testid }) {
  return (
    <textarea
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="bloom-input"
      style={{ resize: "vertical", lineHeight: 1.6 }}
      data-testid={testid}
    />
  );
}

function Select({ value, onChange, options, placeholder, testid }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="bloom-input"
      data-testid={testid}
    >
      <option value="">{placeholder || "Select…"}</option>
      {options.map((o) => (
        <option key={o.value || o} value={o.value || o}>{o.label || o}</option>
      ))}
    </select>
  );
}

function ChipInput({ values, onChange, placeholder, testid }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (!v) return;
    if (!(values || []).includes(v)) onChange([...(values || []), v]);
    setInput("");
  };
  return (
    <div data-testid={testid}>
      <div className="flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="bloom-input"
        />
        <button type="button" onClick={add} className="bloom-btn bloom-btn-outline">
          <Plus size={14} /> Add
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {(values || []).map((v, i) => (
          <span key={i} className="inline-flex items-center gap-2 border border-[var(--bloom-border)] px-3 py-1 text-xs">
            {v}
            <button type="button" onClick={() => onChange(values.filter((_, idx) => idx !== i))}>
              <X size={12} />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-start gap-3 border border-[var(--bloom-border)] p-4 cursor-pointer hover:bg-[var(--bloom-surface)] transition" data-testid={testid}>
      <input
        type="checkbox"
        className="bloom-check mt-0.5"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="text-sm leading-snug">{label}</span>
    </label>
  );
}

// ----------------- Steps -----------------
const StepAbout = ({ data, set }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="step-about">
    <Field label="Full legal name" testid="field-full-name">
      <Text value={data.full_legal_name} onChange={(v) => set({ full_legal_name: v })} placeholder="As shown on your ID" testid="input-full-name" />
    </Field>
    <Field label="Phone" testid="field-phone">
      <Text value={data.phone} onChange={(v) => set({ phone: v })} placeholder="+64 ___ ___ ____" testid="input-phone" />
    </Field>
    <Field label="Farm / business name" testid="field-farm-name">
      <Text value={data.farm_name} onChange={(v) => set({ farm_name: v })} placeholder="e.g. Whitaker Heritage Farm" testid="input-farm-name" />
    </Field>
    <Field label="Land ownership" testid="field-ownership">
      <Select
        value={data.ownership}
        onChange={(v) => set({ ownership: v })}
        options={[
          { value: "owned", label: "I own the land" },
          { value: "leased", label: "I lease the land" },
          { value: "shared", label: "Shared / family land" },
          { value: "other", label: "Other arrangement" },
        ]}
        testid="input-ownership"
      />
    </Field>
    <div className="md:col-span-2">
      <Field label="Property address" testid="field-address">
        <Text value={data.address_line} onChange={(v) => set({ address_line: v })} placeholder="Street address" testid="input-address" />
      </Field>
    </div>
    <Field label="Town / City" testid="field-town">
      <Text value={data.town_city} onChange={(v) => set({ town_city: v })} placeholder="" testid="input-town" />
    </Field>
    <Field label="Region" testid="field-region">
      <Select value={data.region} onChange={(v) => set({ region: v })} options={NZ_REGIONS} placeholder="Select region" testid="input-region" />
    </Field>
    <Field label="Postcode" testid="field-postcode">
      <Text value={data.postcode} onChange={(v) => set({ postcode: v })} placeholder="" testid="input-postcode" />
    </Field>
  </div>
);

const StepLand = ({ data, set }) => (
  <div className="space-y-8" data-testid="step-land">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <Field label="Growing area (sqm)" hint="Approximate productive area" testid="field-growing-area">
        <Text type="number" value={data.growing_area_sqm} onChange={(v) => set({ growing_area_sqm: v })} placeholder="e.g. 600" testid="input-growing-area" />
      </Field>
      <Field label="Years growing (any crop)" testid="field-years-growing">
        <Text type="number" value={data.years_growing} onChange={(v) => set({ years_growing: v })} placeholder="" testid="input-years-growing" />
      </Field>
      <Field label="Years growing dahlias" testid="field-dahlia-years">
        <Text type="number" value={data.dahlia_years} onChange={(v) => set({ dahlia_years: v })} placeholder="" testid="input-dahlia-years" />
      </Field>
    </div>

    <Field label="Crops you currently grow" hint="Add each crop and press enter" testid="field-crops">
      <ChipInput values={data.crops_grown} onChange={(v) => set({ crops_grown: v })} placeholder="e.g. Dahlias" testid="input-crops" />
    </Field>

    <Field label="Where do you currently sell your flowers?" hint="e.g. Farmers markets, florists, weddings. Add each one." testid="field-outlets">
      <ChipInput values={data.current_outlets} onChange={(v) => set({ current_outlets: v })} placeholder="e.g. Mt Eden Farmers Market" testid="input-outlets" />
    </Field>

    <Field label="Describe your current scale of operation" hint="A short paragraph. How many beds? Solo or with help? Year-round or seasonal?" testid="field-scale">
      <Textarea value={data.scale_description} onChange={(v) => set({ scale_description: v })} placeholder="Tell us how your farm runs today…" testid="input-scale" />
    </Field>
  </div>
);

const StepLogistics = ({ data, set }) => (
  <div className="space-y-8" data-testid="step-logistics">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Toggle checked={data.has_refrigeration} onChange={(v) => set({ has_refrigeration: v })} label="I have on-site refrigeration suitable for fresh flowers (cool room, walk-in chiller, or large fridge)." testid="toggle-refrigeration" />
      <Toggle checked={data.has_packing_space} onChange={(v) => set({ has_packing_space: v })} label="I have a clean, dedicated space for packing Bloom Boxes." testid="toggle-packing" />
      <Toggle checked={data.has_reliable_vehicle} onChange={(v) => set({ has_reliable_vehicle: v })} label="I have a reliable vehicle suitable for delivering Bloom Boxes." testid="toggle-vehicle" />
      <Toggle checked={data.can_deliver_locally} onChange={(v) => set({ can_deliver_locally: v })} label="I am able and willing to deliver locally each week of the season." testid="toggle-delivery" />
    </div>

    <Field label="Local delivery radius (km)" hint="How far from your farm can you reasonably deliver?" testid="field-radius">
      <Text type="number" value={data.delivery_radius_km} onChange={(v) => set({ delivery_radius_km: v })} placeholder="e.g. 25" testid="input-radius" />
    </Field>
  </div>
);

const StepPlan = ({ data, set }) => (
  <div className="space-y-8" data-testid="step-plan">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Field label="Target subscribers (Year 1)" hint="How many Bloom Club members would you like to serve?" testid="field-target-subscribers">
        <Text type="number" value={data.target_subscribers} onChange={(v) => set({ target_subscribers: v })} placeholder="e.g. 20" testid="input-target-subscribers" />
      </Field>
      <Field label="Realistic weekly capacity (boxes)" hint="Maximum Bloom Boxes you could pack in your busiest week" testid="field-weekly-capacity">
        <Text type="number" value={data.weekly_capacity_boxes} onChange={(v) => set({ weekly_capacity_boxes: v })} placeholder="e.g. 35" testid="input-weekly-capacity" />
      </Field>
      <Field label="Season starts (month)" testid="field-season-start">
        <Select value={data.season_start_month} onChange={(v) => set({ season_start_month: v })} options={MONTHS} testid="input-season-start" />
      </Field>
      <Field label="Season ends (month)" testid="field-season-end">
        <Select value={data.season_end_month} onChange={(v) => set({ season_end_month: v })} options={MONTHS} testid="input-season-end" />
      </Field>
    </div>
  </div>
);

const StepReferences = ({ data, set }) => {
  const refs = data.references || [];
  const update = (i, patch) => {
    const next = refs.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    set({ references: next });
  };
  const add = () => set({ references: [...refs, { name: "", role: "", email: "", phone: "", relationship: "" }] });
  const remove = (i) => set({ references: refs.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-6" data-testid="step-references">
      <p className="font-serif italic text-[var(--bloom-ink-soft)] -mt-2">
        Two references minimum. Florists, market managers, chefs, regional council, or another grower
        who knows your work all qualify.
      </p>
      {refs.map((r, i) => (
        <div key={i} className="border border-[var(--bloom-border)] p-6" data-testid={`reference-${i}`}>
          <div className="flex justify-between items-center mb-4">
            <div className="bloom-label">Reference {i + 1}</div>
            {refs.length > 2 && (
              <button type="button" onClick={() => remove(i)} className="text-xs flex items-center gap-1 text-[var(--bloom-accent)]" data-testid={`reference-remove-${i}`}>
                <Trash2 size={12} /> Remove
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Full name" testid={`reference-${i}-field-name`}>
              <Text value={r.name} onChange={(v) => update(i, { name: v })} placeholder="" testid={`reference-${i}-name`} />
            </Field>
            <Field label="Role / business" testid={`reference-${i}-field-role`}>
              <Text value={r.role} onChange={(v) => update(i, { role: v })} placeholder="" testid={`reference-${i}-role`} />
            </Field>
            <Field label="Email" testid={`reference-${i}-field-email`}>
              <Text value={r.email} onChange={(v) => update(i, { email: v })} placeholder="" testid={`reference-${i}-email`} />
            </Field>
            <Field label="Phone" testid={`reference-${i}-field-phone`}>
              <Text value={r.phone} onChange={(v) => update(i, { phone: v })} placeholder="" testid={`reference-${i}-phone`} />
            </Field>
            <div className="md:col-span-2">
              <Field label="How do they know you?" testid={`reference-${i}-field-relationship`}>
                <Text value={r.relationship} onChange={(v) => update(i, { relationship: v })} placeholder="e.g. Florist client for 3 years" testid={`reference-${i}-relationship`} />
              </Field>
            </div>
          </div>
        </div>
      ))}
      <button type="button" onClick={add} className="bloom-btn bloom-btn-outline" data-testid="add-reference-btn">
        <Plus size={14} /> Add Another Reference
      </button>
    </div>
  );
};

const StepStory = ({ data, set }) => (
  <div className="space-y-8" data-testid="step-story">
    <Field label="Why do you want to join the Bloom Club?" hint="Min 60 characters. Speak from the heart." testid="field-why">
      <Textarea rows={5} value={data.why_join} onChange={(v) => set({ why_join: v })} placeholder="What draws you to the Heritage Bloom Club model?" testid="input-why" />
      <CharCount value={data.why_join} min={60} />
    </Field>
    <Field label="Your vision for your Bloom Club presence" testid="field-vision">
      <Textarea rows={4} value={data.vision_statement} onChange={(v) => set({ vision_statement: v })} placeholder="What do you imagine your subscribers experiencing season after season?" testid="input-vision" />
      <CharCount value={data.vision_statement} min={80} />
    </Field>
    <Field label="What makes your farm uniquely yours?" testid="field-strengths">
      <Textarea rows={4} value={data.unique_strengths} onChange={(v) => set({ unique_strengths: v })} placeholder="Heirloom varieties, growing methods, story…" testid="input-strengths" />
      <CharCount value={data.unique_strengths} min={60} />
    </Field>
  </div>
);

function CharCount({ value, min }) {
  const len = (value || "").length;
  const ok = len >= min;
  return (
    <div className={`text-xs mt-1 font-serif italic ${ok ? "text-[var(--bloom-brand)]" : "text-[var(--bloom-ink-muted)]"}`}>
      {len} characters · {ok ? "✓" : `${min} minimum`}
    </div>
  );
}

const StepPhotos = ({ data, set }) => {
  const [busy, setBusy] = useState(false);

  const onFiles = async (files) => {
    setBusy(true);
    const next = [...(data.photos || [])];
    for (const file of files) {
      if (!file.type.startsWith("image/")) continue;
      try {
        const dataUrl = await compressImage(file, 1400, 0.78);
        next.push({ data_url: dataUrl, caption: file.name.replace(/\.[^.]+$/, "") });
      } catch (err) {
        // Image compression can fail on corrupted files; skip this one and
        // keep processing the rest of the batch.
        console.warn("Skipped photo, could not compress:", file?.name, err?.message);
      }
      if (next.length >= 8) break;
    }
    set({ photos: next });
    setBusy(false);
  };

  const remove = (i) => set({ photos: data.photos.filter((_, idx) => idx !== i) });
  const setCaption = (i, c) => set({ photos: data.photos.map((p, idx) => idx === i ? { ...p, caption: c } : p) });

  return (
    <div data-testid="step-photos">
      <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-6">
        Add 1 to 8 photos of your farm, growing space, packing area, and a recent harvest.
        Photos are auto-resized to ~1400px.
      </p>

      <label className="border border-dashed border-[var(--bloom-border)] p-10 flex flex-col items-center justify-center gap-3 cursor-pointer hover:bg-[var(--bloom-surface)] transition" data-testid="photo-dropzone">
        <Upload className="text-[var(--bloom-ink-muted)]" strokeWidth={1.5} />
        <div className="text-sm">{busy ? "Processing…" : "Click to add photos"}</div>
        <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)]">JPG · PNG · up to 8 photos</div>
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => onFiles(Array.from(e.target.files || []))}
          className="hidden"
          data-testid="photo-input"
        />
      </label>

      {(data.photos || []).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-8" data-testid="photo-grid">
          {data.photos.map((p, i) => (
            <div key={i} className="border border-[var(--bloom-border)] p-2" data-testid={`photo-${i}`}>
              <div className="relative aspect-[4/3] overflow-hidden bg-[var(--bloom-surface)]">
                <img src={p.data_url} alt="" className="w-full h-full object-cover" />
                <button onClick={() => remove(i)} className="absolute top-2 right-2 bg-[var(--bloom-bg)] border border-[var(--bloom-border)] p-1" data-testid={`photo-remove-${i}`}>
                  <X size={12} />
                </button>
              </div>
              <input
                value={p.caption || ""}
                onChange={(e) => setCaption(i, e.target.value)}
                placeholder="Caption…"
                className="bloom-input text-xs mt-2"
                data-testid={`photo-caption-${i}`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

async function compressImage(file, maxDim = 1400, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result; };
    reader.onerror = reject;
    img.onload = () => {
      const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const StepReview = ({ data, missing }) => (
  <div className="space-y-8" data-testid="step-review">
    {missing && missing.length > 0 ? (
      <div className="border border-[var(--bloom-accent)] p-6 bg-[var(--bloom-surface)]" data-testid="missing-list">
        <div className="flex items-center gap-2 mb-3">
          <AlertCircle className="text-[var(--bloom-accent)]" size={18} />
          <h3 className="font-serif text-xl">Before you submit</h3>
        </div>
        <ul className="text-sm space-y-1">
          {missing.map((m, i) => (
            <li key={i} className="font-serif italic">· {m}</li>
          ))}
        </ul>
      </div>
    ) : (
      <div className="border border-[var(--bloom-brand)] p-6 bg-[var(--bloom-surface)]" data-testid="ready-banner">
        <div className="flex items-center gap-2 mb-2">
          <Check className="text-[var(--bloom-brand)]" size={18} />
          <h3 className="font-serif text-xl">Your application is complete.</h3>
        </div>
        <p className="font-serif italic text-[var(--bloom-ink-soft)] text-sm">
          Review the summary below and press <em>Submit Application</em> when ready. Once submitted,
          you will not be able to edit unless we request more information.
        </p>
      </div>
    )}

    <ReviewBlock title="About You">
      <ReviewRow label="Legal name" value={data.full_legal_name} />
      <ReviewRow label="Phone" value={data.phone} />
      <ReviewRow label="Farm" value={data.farm_name} />
      <ReviewRow label="Ownership" value={data.ownership} />
      <ReviewRow label="Location" value={[data.address_line, data.town_city, data.region, data.postcode].filter(Boolean).join(", ")} />
    </ReviewBlock>

    <ReviewBlock title="Land & Experience">
      <ReviewRow label="Growing area" value={data.growing_area_sqm ? `${data.growing_area_sqm} sqm` : ""} />
      <ReviewRow label="Years growing" value={data.years_growing} />
      <ReviewRow label="Dahlia years" value={data.dahlia_years} />
      <ReviewRow label="Crops" value={(data.crops_grown || []).join(", ")} />
      <ReviewRow label="Current outlets" value={(data.current_outlets || []).join(", ")} />
      <ReviewRow label="Scale" value={data.scale_description} multiline />
    </ReviewBlock>

    <ReviewBlock title="Logistics & Plan">
      <ReviewRow label="Refrigeration" value={data.has_refrigeration ? "Yes" : "No"} />
      <ReviewRow label="Packing space" value={data.has_packing_space ? "Yes" : "No"} />
      <ReviewRow label="Reliable vehicle" value={data.has_reliable_vehicle ? "Yes" : "No"} />
      <ReviewRow label="Local delivery" value={data.can_deliver_locally ? `Yes, ${data.delivery_radius_km}km` : "No"} />
      <ReviewRow label="Target subscribers" value={data.target_subscribers} />
      <ReviewRow label="Weekly capacity" value={data.weekly_capacity_boxes ? `${data.weekly_capacity_boxes} boxes` : ""} />
      <ReviewRow label="Season" value={[data.season_start_month, data.season_end_month].filter(Boolean).join("·")} />
    </ReviewBlock>

    <ReviewBlock title="References">
      {(data.references || []).map((r, i) => (
        <div key={i} className="mb-3">
          <div className="font-serif">{r.name || "·"} <span className="text-[var(--bloom-ink-muted)] text-sm">· {r.role}</span></div>
          <div className="text-xs text-[var(--bloom-ink-muted)]">{r.email} {r.phone && `· ${r.phone}`}</div>
          {r.relationship && <div className="text-xs italic">{r.relationship}</div>}
        </div>
      ))}
    </ReviewBlock>

    <ReviewBlock title="Your Story">
      <ReviewRow label="Why join" value={data.why_join} multiline />
      <ReviewRow label="Vision" value={data.vision_statement} multiline />
      <ReviewRow label="Strengths" value={data.unique_strengths} multiline />
    </ReviewBlock>

    <ReviewBlock title={`Photos (${(data.photos || []).length})`}>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {(data.photos || []).map((p, i) => (
          <div key={i} className="aspect-square overflow-hidden border border-[var(--bloom-border)]">
            <img src={p.data_url} alt={p.caption} className="w-full h-full object-cover" />
          </div>
        ))}
      </div>
    </ReviewBlock>
  </div>
);

function ReviewBlock({ title, children }) {
  return (
    <div className="border-t border-[var(--bloom-border)] pt-6">
      <h4 className="font-serif text-2xl mb-4">{title}</h4>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function ReviewRow({ label, value, multiline }) {
  if (!value && value !== 0) return null;
  return (
    <div className={multiline ? "" : "flex justify-between gap-6"}>
      <div className="bloom-label">{label}</div>
      <div className={`font-serif ${multiline ? "italic mt-1 leading-relaxed" : "text-right"}`} style={multiline ? {} : { maxWidth: "60%" }}>{String(value)}</div>
    </div>
  );
}

const STEP_RENDERERS = {
  about: StepAbout,
  land: StepLand,
  logistics: StepLogistics,
  plan: StepPlan,
  references: StepReferences,
  story: StepStory,
  photos: StepPhotos,
  review: StepReview,
};
